import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { DRAG_DROP_PASTE } from "@lexical/rich-text"
import { isMimeType, mediaFileReader } from "@lexical/utils"
import { COMMAND_PRIORITY_LOW } from "lexical"
import { useEffect, useState } from "react"
import { MediaType } from "@prisma/client"
import { toast } from "react-toastify"
import { useLibraryTemplateSelector } from "store/hooks"
import { useTranslation } from "react-i18next"
import useMediaUpload from "hooks/useMediaUpload"
import { educationalContentMediaUploadService } from "utils/library-template/educationalContentMediaUploadService"

const ACCEPTABLE_IMAGE_TYPES = ["image/", "image/heic", "image/heif", "image/gif", "image/webp"]
const ACCEPTABLE_VIDEO_TYPES = ["video/", "video/mp4", "video/quicktime", "video/x-msvideo"]

export default function DragDropPaste(): null {
    const [editor] = useLexicalComposerContext()
    const { libraryTemplate, educationalContent } = useLibraryTemplateSelector()
    const { t } = useTranslation("common")
    const [isUploading, setIsUploading] = useState(false)
    const { uploadImage, uploadVideo } = useMediaUpload()

    const handleFileUpload = async (file: File, isImage: boolean) => {
        await educationalContentMediaUploadService({
            file,
            mediaType: isImage ? MediaType.IMAGE : MediaType.VIDEO,
            libraryTemplate,
            educationalContent,
            uploadImage,
            uploadVideo,
            t,
            editor
        })
    }

    useEffect(() => {
        return editor.registerCommand(
            DRAG_DROP_PASTE,
            (files) => {
                if (isUploading) return true
                ;(async () => {
                    setIsUploading(true)

                    try {
                        // Handle Images
                        const imageFiles = await mediaFileReader(
                            files,
                            [ACCEPTABLE_IMAGE_TYPES].flatMap((x) => x)
                        )

                        for (const { file } of imageFiles) {
                            if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
                                try {
                                    await handleFileUpload(file, true)
                                } catch (error) {
                                    console.error("Error uploading image:", error)
                                    toast(t("wentWrong"), { type: "error" })
                                }
                            }
                        }

                        // Handle Videos
                        const videoFiles = await mediaFileReader(
                            files,
                            [ACCEPTABLE_VIDEO_TYPES].flatMap((x) => x)
                        )

                        for (const { file } of videoFiles) {
                            if (isMimeType(file, ACCEPTABLE_VIDEO_TYPES)) {
                                try {
                                    await handleFileUpload(file, false)
                                } catch (error) {
                                    console.error("Error uploading video:", error)
                                    toast(t("wentWrong"), { type: "error" })
                                }
                            }
                        }
                    } catch (error) {
                        console.error("Error in drag-drop handler:", error)
                        toast(t("wentWrong"), { type: "error" })
                    } finally {
                        setIsUploading(false)
                    }
                })()

                return true
            },
            COMMAND_PRIORITY_LOW
        )
    }, [editor, libraryTemplate, educationalContent, isUploading, t, uploadImage, uploadVideo])

    return null
}
